import { useReactiveVar } from '@apollo/client';
import React, { useState, useEffect } from 'react';

import TableHeader from '@/components/TableHeader';
import Table from '@/components/Table';
import { TableRow, TableCell } from '@/elements/Table';
import Card from '@/components/Card';
import LoadingState from '@/components/LoadingState';
import Page from '@/components/Page';
import Stack from '@/components/Stack';
import { Body } from '@/components/Typography';
import TextStack from '@/components/TextStack';
import useMediaQuery from '@/hooks/useMediaQuery';
import { currentAgencyVar } from '@/util/apollo/cache';
import { axiosRubyClient } from '@/util/axios/axiosClient';
import { format } from 'date-fns';
import Chip from '@/components/Chip';
import Link from '@/elements/Link';
import IconicButton from '@/components/IconicButton';
import { faEye } from '@fortawesome/free-solid-svg-icons';
import ReportModal from './ReportModal';
import TextInput from '@/components/TextInput';
import Pagination from '@/components/Pagination';
import Config from '@/config';
import { IS_PROD } from '@/constants/env';

const HEADER_FIELDS = [
  { name: 'ID', style: { width: '7%' } },
  { name: 'WORKER ID', style: { width: '20%', color: 'black' } },
  { name: 'COMPLIANCE ID', style: { width: '12%', color: 'black' } },
  { name: 'PREVIOUS STATUS', style: { width: '12%', color: 'black' } },
  { name: 'NEW STATUS', style: { width: '12%', color: 'black' } },
  { name: 'NOTES', style: { width: '20%', color: 'black' } },
  { name: 'DATE', style: { width: '20%', color: 'black' } },
  { name: 'ACTIONS', style: { width: '5%', color: 'black' } },
];

type BackgroundCheckLog = {
  id: string;
  worker_id: number;
  compliance_id: number | null;
  report_data: any;
  previous_status: string;
  new_status: string;
  notes: string;
  created_at: string;
  updated_at: string;
  worker: {
    user: {
      first_name: string;
      middle_name: string;
      last_name: string;
    };
  };
};

type PaginationData = {
  total_count: number;
  current_page: number;
  per_page: number;
  total_pages: number;
};

const formatDate = (dateString: string) => {
  try {
    return format(new Date(dateString), 'MMM dd, yyyy HH:mm');
  } catch (e) {
    return dateString;
  }
};

const getStatusChip = (status: string) => {
  if (!status) return <Chip label="Unknown" size="small" />;

  switch (status.toLowerCase()) {
    case 'approved':
      return <Chip color="success" label="Approved" size="small" />;
    case 'pending':
      return <Chip color="warning" label="Pending" size="small" />;
    case 'needs_review':
      return <Chip color="error" label="Needs Review" size="small" />;
    default:
      return (
        <Chip
          style={{ textTransform: 'capitalize' }}
          label={status}
          size="small"
        />
      );
  }
};

const BackgroundAudit: React.FC = () => {
  const currentAgency = useReactiveVar(currentAgencyVar);
  const phoneOnly = useMediaQuery('(max-width: 559px)');
  const [loading, setLoading] = useState(true);
  const [backgroundLogs, setBackgroundLogs] = useState<BackgroundCheckLog[]>(
    [],
  );
  const [error, setError] = useState<string | null>(null);
  const [selectedReportData, setSelectedReportData] = useState<any>(null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [pagination, setPagination] = useState<PaginationData>({
    total_count: 0,
    current_page: 1,
    per_page: 25,
    total_pages: 0,
  });
  const [query, setQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    fetchBackgroundLogs();
  }, []);

  // Debounce search with 500ms delay
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchInput !== query) {
        setQuery(searchInput);
        setPagination((prev) => ({ ...prev, current_page: 1 })); // Reset to first page on search
        fetchBackgroundLogs(searchInput, 1);
      }
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [searchInput, query]);

  const fetchBackgroundLogs = async (
    searchQuery?: string,
    page: number = 1,
  ) => {
    setLoading(true);
    try {
      const response = await axiosRubyClient.get('/api/background_check_logs', {
        params: {
          query: searchQuery || query,
          page,
          per_page: pagination.per_page,
        },
      });
      setBackgroundLogs(response.data.data);

      // Update pagination data from response
      if (response.data.pagination) {
        setPagination({
          total_count: response.data.pagination.total_count,
          current_page: response.data.pagination.current_page,
          per_page: response.data.pagination.per_page || pagination.per_page,
          total_pages: Math.ceil(
            response.data.pagination.total_count /
              (response.data.pagination.per_page || pagination.per_page),
          ),
        });
      }

      setError(null);
    } catch (error) {
      console.error('Error fetching background check logs:', error);
      setError('Error loading background check logs');
    } finally {
      setLoading(false);
    }
  };

  const handleShowReport = (reportData: any) => {
    // Add debugger statement to help diagnose the issue
    debugger;
    console.log('Showing report data:', reportData);
    setSelectedReportData(reportData);
    setShowReportModal(true);
  };

  const baseUrl = IS_PROD
    ? 'https://dashboard.checkr.com'
    : 'https://dashboard.checkrhq-staging.net';

  const handleHideModal = () => {
    setShowReportModal(false);
    setSelectedReportData(null);
  };

  const onQueryChange = (value: string) => {
    setSearchInput(value);
  };

  const onPageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, current_page: page }));
    fetchBackgroundLogs(query, page);
  };

  return (
    <Page
      size="full"
      title="Background Audit"
      primaryAction={{
        a11yLabel: 'Refresh',
        label: 'Refresh',
        onAction: () => fetchBackgroundLogs(query, pagination.current_page),
      }}
    >
      <Card>
        <Card.Section>
          <Stack gap={phoneOnly ? 16 : 8} vertical={phoneOnly}>
            <div style={{ flex: 1 }}>
              <TextInput
                id="input-search-worker"
                placeholder={`Search for GravyWorkers...`}
                value={searchInput}
                onChange={(ev) => onQueryChange(ev.currentTarget.value)}
              />
            </div>
          </Stack>
        </Card.Section>

        <Table spacing={false}>
          <TableHeader fields={HEADER_FIELDS} />
          <tbody>
            {!loading && backgroundLogs.length > 0 ? (
              backgroundLogs.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>
                    <Stack gap={20}>
                      <TextStack spacing="tight">
                        <Link
                          to={`${baseUrl}/candidates/${item.report_data.checkr_id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Body color={'theme'} weight="medium">
                            {item.report_data.checkr_id}
                          </Body>
                        </Link>
                      </TextStack>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack gap={20}>
                      <TextStack spacing="tight">
                        <Link
                          id={`link-${item.id}`}
                          to={`/workers/${item.worker_id}`}
                        >
                          <Body color={'theme'} weight="medium">
                            {item.worker.user.first_name}{' '}
                            {item.worker.user.middle_name}{' '}
                            {item.worker.user.last_name}
                          </Body>
                        </Link>
                      </TextStack>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack gap={20}>
                      <TextStack spacing="tight">
                        <Body>{item.compliance_id || 'N/A'}</Body>
                      </TextStack>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack gap={20}>
                      <TextStack spacing="tight">
                        {getStatusChip(item.previous_status)}
                      </TextStack>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack gap={20}>
                      <TextStack spacing="tight">
                        {getStatusChip(item.new_status)}
                      </TextStack>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack gap={20}>
                      <TextStack spacing="tight">
                        <Body>{item.notes}</Body>
                      </TextStack>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack gap={20}>
                      <TextStack spacing="tight">
                        <Body>{formatDate(item.created_at)}</Body>
                      </TextStack>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Stack gap={20}>
                      <TextStack spacing="tight">
                        <IconicButton
                          a11yLabel="View Report Data"
                          appearance="primary"
                          icon={faEye}
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleShowReport(item.report_data);
                          }}
                        />
                      </TextStack>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            ) : !loading ? (
              <TableRow>
                <TableCell
                  colSpan={HEADER_FIELDS.length}
                  style={{ textAlign: 'center', padding: '20px' }}
                >
                  <Body>No background check logs found.</Body>
                </TableCell>
              </TableRow>
            ) : null}
          </tbody>
        </Table>

        {loading && (
          <Stack align="center" justify="center" style={{ padding: '40px 0' }}>
            <LoadingState
              overlayColor="white"
              text="Loading background check logs"
            />
          </Stack>
        )}

        {error && (
          <div style={{ textAlign: 'center', padding: '20px', color: 'red' }}>
            <Body>{error}</Body>
          </div>
        )}

        {/* Pagination */}
        {!loading &&
          backgroundLogs.length > 0 &&
          pagination.total_pages > 1 && (
            <Card.Section>
              <Stack justify="center">
                <Pagination
                  currentPage={pagination.current_page}
                  totalPages={pagination.total_pages}
                  onPageChange={onPageChange}
                />
              </Stack>
            </Card.Section>
          )}
      </Card>

      <ReportModal
        reportData={selectedReportData}
        isOpen={showReportModal}
        onClose={handleHideModal}
      />
    </Page>
  );
};

export default BackgroundAudit;
